import { useRef, useState } from 'react'
import { router, usePage } from '@inertiajs/react'
import { Bot, CheckCircle2, FlaskConical, Loader2, PauseCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'
import { composerDraftKey, writeComposerDraft } from '@/frontend/features/AgentChats/assistant-ui/composerDrafts'
import { createSessionOverHttp } from '@/frontend/pages/Dashboard/AgentChats/api'
import { agentChatHref } from '@/frontend/layouts/worker-first/agentAccess'
import { randomUuid } from '@/frontend/lib/randomUuid'
import { Banner } from './components'
import type { Preview, TriggerAgent } from './types'
import { fill } from './types'

type Copy = Record<string, string>

/** Asks one of the project's agents for a trigger (or a change): the request goes to that agent's new conversation as a
 *  draft. The user sends it; the agent works through the Workstation MCP (its skill has the guide). */
export function AskAgent({ agents, copy, template, title, label, variant = 'default' }: {
  agents: TriggerAgent[]; copy: Copy; template: string; title: string; label: string; variant?: 'default' | 'outline'
}) {
  const props = usePage<{ auth: { user: { id: number | string } | null }; project?: { id: string } }>().props
  const userId = props.auth?.user?.id ?? 'anonymous'
  const [open, setOpen] = useState(false)
  const [workerId, setWorkerId] = useState(agents[0]?.worker_id ?? '')
  const [text, setText] = useState(template)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const submission = useRef(randomUuid())
  const agent = agents.find((a) => a.worker_id === workerId)

  const send = async () => {
    if (!agent || !props.project || busy) return
    setBusy(true); setError(null)
    try {
      // A project session supplies the authenticated MCP context. Keep the
      // request as a draft so opening this dialog never starts an agent run.
      const session = await createSessionOverHttp({ workerId: agent.worker_id, source: 'fde_project',
        fdeProjectId: props.project.id, options: { clientSubmissionId: submission.current } })
      writeComposerDraft(composerDraftKey(userId, agent.worker_id, session.id), text)
      router.visit(agentChatHref(agent.worker_id, { sessionId: session.id }))
    } catch (err) {
      setError(err instanceof Error ? err.message : copy.ask_error)
      setBusy(false)
    }
  }

  return (
    <>
      <Button size="sm" variant={variant} onClick={() => { setText(template); setOpen(true) }}>
        <Bot className="size-4" aria-hidden />{label}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription>{copy.ask_help}</DialogDescription>
          </DialogHeader>
          {agents.length === 0 ? <Banner tone="info">{copy.ask_no_agents}</Banner> : (
            <div className="grid gap-3">
              <div className="grid gap-1.5">
                <span className="text-xs font-medium text-muted-foreground">{copy.ask_agent_label}</span>
                <div className="flex flex-wrap gap-2">
                  {agents.map((a) => (
                    <button key={a.worker_id} type="button" aria-pressed={a.worker_id === workerId} onClick={() => setWorkerId(a.worker_id)}
                      className={cn('rounded-lg border px-3 py-1.5 text-sm transition-colors',
                        a.worker_id === workerId ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground hover:text-foreground')}>
                      {a.name} <span className="text-xs opacity-70">· {copy[`role_${a.role}`] ?? a.role}</span>
                    </button>
                  ))}
                </div>
              </div>
              <label className="grid gap-1.5">
                <span className="text-xs font-medium text-muted-foreground">{copy.ask_request}</span>
                <Textarea value={text} rows={6} onChange={(e) => setText(e.target.value)} />
              </label>
              {error && <Banner tone="error">{error}</Banner>}
            </div>
          )}
          <DialogFooter>
            <Button onClick={send} disabled={!agent || !text.trim() || busy || !props.project}>{copy.ask_open}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

/** The dry run in words: whether the data is ready, how many things match now and what would fire. */
export function PreviewSummary({ preview, copy, labels = {}, start }: { preview: Preview; copy: Copy; labels?: Record<string, string>; start?: 'now' | 'all' }) {
  const gate = preview.gate
  const name = (key: string) => labels[key] ?? key
  return (
    <div className="grid gap-3 text-sm">
      <div className={cn('flex items-start gap-2 rounded-lg px-3 py-2',
        gate?.status === 'open' ? 'bg-emerald-50 text-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-100' : 'bg-amber-50 text-amber-900 dark:bg-amber-950/40 dark:text-amber-100')}>
        {gate?.status === 'open' ? <CheckCircle2 className="mt-0.5 size-4 shrink-0" aria-hidden /> : <PauseCircle className="mt-0.5 size-4 shrink-0" aria-hidden />}
        <div className="grid gap-1">
          <span className="font-medium">{copy[`preview_gate_${gate?.status ?? 'held'}`]}</span>
          {(gate?.reasons ?? []).map((reason) => <span key={reason} className="text-xs">{reason}</span>)}
        </div>
      </div>
      {preview.error ? <Banner tone="error">{preview.error}</Banner> : (
        <>
          <div className="grid grid-cols-2 gap-2">
            <div className="grid gap-0.5 rounded-lg border border-border p-3">
              <span className="text-2xl font-semibold tabular-nums">{(preview.rows ?? 0).toLocaleString('pt-BR')}</span>
              <span className="text-xs text-muted-foreground">{copy.preview_matching}</span>
            </div>
            <div className="grid gap-0.5 rounded-lg border border-border p-3">
              <span className="text-2xl font-semibold tabular-nums">{(preview.would_fire?.events ?? 0).toLocaleString('pt-BR')}</span>
              <span className="text-xs text-muted-foreground">{copy.preview_would_now}</span>
            </div>
          </div>
          {start && <p className="text-xs text-muted-foreground">{fill(start === 'all' ? copy.fire_now_on : copy.fire_now_off, { rows: preview.rows ?? 0 })}</p>}
          {(preview.holds ?? []).length > 0 && (
            <div className="grid gap-1 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:bg-amber-950/40 dark:text-amber-100">
              <span className="font-medium">{copy.preview_holds}</span>
              {(preview.holds ?? []).map((h) => <span key={h}>{h}</span>)}
            </div>
          )}
          {(preview.sample ?? []).length > 0 && (
            <div className="max-h-80 overflow-auto rounded-lg border border-border">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-muted text-left">
                  <tr>{(preview.columns ?? []).map((c) => (
                    <th key={c} className="whitespace-nowrap px-2 py-1.5 font-medium">
                      {name(c)}{(preview.entity ?? []).includes(c) && <span className="ml-1 text-primary" title={copy.identity_label}>•</span>}
                    </th>
                  ))}</tr>
                </thead>
                <tbody>
                  {(preview.sample ?? []).slice(0, 12).map((row, index) => (
                    <tr key={index} className="border-t border-border">
                      {(preview.columns ?? []).map((c) => <td key={c} className="whitespace-nowrap px-2 py-1 tabular-nums">{row[c] == null ? '—' : String(row[c])}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {preview.query && (
            <details className="text-xs">
              <summary className="cursor-pointer text-muted-foreground hover:text-foreground">{copy.preview_query}</summary>
              <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap rounded-lg bg-muted/50 p-3 font-mono text-[11px] leading-relaxed">{preview.query}</pre>
            </details>
          )}
        </>
      )}
    </div>
  )
}

export function SimulateButton({ busy, copy, onRun }: { busy: boolean; copy: Copy; onRun: () => void }) {
  return (
    <Button size="sm" variant="outline" onClick={onRun} disabled={busy}>
      {busy ? <Loader2 className="size-4 animate-spin" aria-hidden /> : <FlaskConical className="size-4" aria-hidden />}
      {busy ? copy.simulating : copy.simulate}
    </Button>
  )
}
